import { ProfessionalRole } from '@app/user-management/types/professional-role'
import { AccessRole } from '@app/user-management/types/access-role'
import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable, catchError, expand, map, of, reduce, throwError } from 'rxjs'
import { ListUsersQueryDTO } from '../dto/list-users/list-users-query.dto'
import { GetUserByEmailQueryResponseDTO, ListUsersQueryResponseDTO } from '../dto/list-users/list-users-query-response.dto'
import { PlatformAccessService } from '@app/common/services/platform-access.service'
import { CreateUserDTO } from '../dto/create-users/create-user.dto'
import { CreateUsersResponseDTO } from '../dto/create-users/create-users-response.dto'
import { CreateUsersRequestDTO } from '../dto/create-users/create-users-request.dto'
import { ArchiveUsersRequestDTO } from '../dto/archive-users/archive-users'
import { User } from '../types/user'
import { UnarchiveUsersRequestDTO } from '../dto/archive-users/unarchive-users'
import { UserActivityResponseDTO } from '../dto/user-activity/user-activity-response.dto'
import { UserActivityQueryDTO } from '../dto/user-activity/user-activity-query.dto'
import { RouterAdapterService } from '@app/common/router/router-adapter.service'
import { NoAvailableLicensesError } from '../errors/no-available-licenses-error'
import { UserLicenseRequestError } from '../errors/user-license-request.error'
import { UserServiceApiErrorPayload } from '../types/user-service-api-error-payload'
import { UUIDv4 } from '@app/common/types/uuid'
import { MoveUsersRequestDTO } from '../dto/move-users/move-users-request.dto'
import { MoveUsersResponseDTO } from '../dto/move-users/move-users-response.dto'
import { BillingPlatform } from '@app/organization-management/types/billing-platform'
import { OrderType } from '@app/organization-management/types/order-type'

export const NO_AVAILABLE_LICENSES_ERROR_CODE = 'user-assign-licenses-not-enough-licenses'

/**
 * Sentinel passed in place of an organization UUID to request a system-wide
 * (cross-tenant) users list. Today this is interpolated directly into the
 * path: GET /organizations/*\/users — backend support for this shape needs
 * confirmation.
 */
export const ALL_ORGS_SENTINEL = '*'

export type UserLicense = { licensePoolId: string } | { licenseTypePlanId: string }

/** Only the profile fields that changed. */
export interface UserProfileChanges {
  firstName?: string
  lastName?: string
  professionalRole?: ProfessionalRole
}

export interface MoveUsersParams {
  userIds: UUIDv4[]
  targetOrganizationId: UUIDv4
  licenseTypePlanId?: UUIDv4
  licensePoolId?: UUIDv4
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly platformAccess: PlatformAccessService = inject(PlatformAccessService)
  private readonly routerAdapter = inject(RouterAdapterService)

  createUsers(users: CreateUserDTO[], organizationId?: string): Observable<CreateUsersResponseDTO> {
    const createUsersRequest = new CreateUsersRequestDTO(users)
    const orgId = organizationId || this.routerAdapter.routeOrganizationId()

    return this.platformAccess
      .post<CreateUsersResponseDTO>(`/organizations/${orgId}/users`, createUsersRequest)
      .pipe(catchError(error => this.handleLicenseAvailabilityError(error)))
  }

  moveUsers({ userIds, targetOrganizationId, licenseTypePlanId, licensePoolId }: MoveUsersParams): Observable<MoveUsersResponseDTO> {
    const body = new MoveUsersRequestDTO(userIds, licenseTypePlanId, licensePoolId)

    return this.platformAccess.post<MoveUsersResponseDTO>(`/organizations/${targetOrganizationId}/users/move`, body)
  }

  /** Body names the license the way the organization's billing platform does. */
  assignUserLicense(userId: string, license: UserLicense, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess
      .put<void>(`/organizations/${orgId}/users/${userId}/license`, license)
      .pipe(catchError(error => this.toUserRequestError(error)))
  }

  revokeUserLicense(userId: string, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess
      .delete<void>(`/organizations/${orgId}/users/${userId}/license`)
      .pipe(catchError(error => this.toUserRequestError(error)))
  }

  updateUserProfile(userId: string, changes: UserProfileChanges, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess
      .patch<void>(`/organizations/${orgId}/users/${userId}/profile`, changes)
      .pipe(catchError(error => this.toUserRequestError(error)))
  }

  /** Speechy Admin only. A `500` with a role-saved code means the role itself was saved. */
  changeUserAccessRole(userId: string, accessRole: AccessRole, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess
      .put<void>(`/organizations/${orgId}/users/${userId}/access-role`, { accessRole })
      .pipe(catchError(error => this.toUserRequestError(error)))
  }

  updateUserEmail(userId: string, email: string, organizationId?: string): Observable<void> {
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.post<void>(`/organizations/${orgId}/users/${userId}/change-email`, { email })
  }

  listUsers(query?: ListUsersQueryDTO, organizationId?: string): Observable<ListUsersQueryResponseDTO> {
    const orgId = organizationId || this.routerAdapter.routeOrganizationId()
    // When orgId === ALL_ORGS_SENTINEL ('*'), the resulting URL is
    // /organizations/*/users — this is the system-wide list path used by the
    // /admin/users/all page.
    return (this.platformAccess.get(`/organizations/${orgId}/users`, query) as Observable<ListUsersQueryResponseDTO>).pipe(
      map(response => ({ ...response, users: response.users.map(u => this.hydrateUser(u)) }))
    )
  }

  listAllUsers(query: ListUsersQueryDTO, organizationId?: string): Observable<User[]> {
    query.page = 1
    query.pageSize = 100
    return this.listUsers(query, organizationId).pipe(
      expand(response => {
        if (response.page < response.totalPages) {
          return this.listUsers({ ...query, page: response.page + 1 }, organizationId)
        }
        return []
      }),
      map(response => response.users),
      reduce((acc: User[], users: User[]) => acc.concat(users), [])
    )
  }

  getUserById(id: string, organizationId = '11111111-1111-1111-1111-111111111111'): Observable<User | undefined> {
    const orgId = this.routerAdapter.routeOrganizationId() || organizationId
    return (this.platformAccess.get(`/organizations/${orgId}/users/${id}`) as Observable<ListUsersQueryResponseDTO>).pipe(
      map((response: ListUsersQueryResponseDTO) => {
        const rawUser = response.users?.[0]
        return rawUser ? this.hydrateUser(rawUser) : undefined
      })
    )
  }

  getUserByEmail(email: string, organizationId = '11111111-1111-1111-1111-111111111111'): Observable<User | null> {
    const queryParams = {
      email: email,
    }
    const orgId = this.routerAdapter.routeOrganizationId() || organizationId
    return (this.platformAccess.get(`/organizations/${orgId}/users/by-email`, queryParams) as Observable<GetUserByEmailQueryResponseDTO>).pipe(
      map((response: GetUserByEmailQueryResponseDTO) => {
        const user = response.users[0]
        return user ? this.hydrateUser(user) : null
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null)
        }
        return throwError(() => err)
      })
    )
  }

  archiveUsers(userIds: string[], organizationId?: string) {
    const archiveUsersRequest = new ArchiveUsersRequestDTO(userIds)
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.put(`/organizations/${orgId}/users/archive`, archiveUsersRequest)
  }

  unarchiveUsers(userIds: string[], organizationId?: string) {
    const archiveUsersRequest = new UnarchiveUsersRequestDTO(userIds)
    const orgId = this.resolveOrganizationId(organizationId)
    return this.platformAccess.put(`/organizations/${orgId}/users/unarchive`, archiveUsersRequest)
  }

  getUserActivityData(query: UserActivityQueryDTO): Observable<UserActivityResponseDTO | undefined> {
    const orgId = this.routerAdapter.routeOrganizationId()
    return this.platformAccess.get(`/organizations/${orgId}/users/${query.userId}/activity`, {
      sortDirection: query.sortDirection,
    }) as Observable<UserActivityResponseDTO | undefined>
  }

  /**
   * Derives the customer-facing order type for a user based on their
   * organization's billing platform and (for Chargify) the customer's
   * `orderType` field. Mirrors the logic previously inlined in
   * `ExistingUserInfoCardComponent` so any component can rely on a single
   * source of truth via `user.orderType`.
   */
  getOrderType(user: User): OrderType {
    const billingPlatform = user.organization?.billingPlatform
    if (!billingPlatform) {
      return OrderType.NOT_AVAILABLE
    }

    if (billingPlatform === BillingPlatform.MAXIO) {
      return OrderType.REMITTANCE
    }

    const customerOrderType = user.organization?.customer?.orderType
    if (!customerOrderType) {
      return OrderType.NOT_AVAILABLE
    }
    return customerOrderType.toLowerCase() === 'web' ? OrderType.AUTOMATIC : OrderType.REMITTANCE
  }

  private toUserRequestError(error: unknown): Observable<never> {
    if (!(error instanceof HttpErrorResponse)) return throwError(() => error)
    const body = (error.error ?? {}) as { code?: string; message?: string | string[] }
    const message = Array.isArray(body.message) ? body.message.join(', ') : body.message
    return throwError(() => new UserLicenseRequestError(message || error.message, body.code))
  }

  private resolveOrganizationId(organizationId?: string): string {
    const orgId = organizationId ?? this.routerAdapter.routeOrganizationId()
    if (!orgId) throw new Error('Organization id is required for user mutation requests')
    return orgId
  }

  private hydrateUser(rawUser: User): User {
    const user = Object.assign(new User(), rawUser)
    user.orderType = this.getOrderType(user)
    return user
  }

  private handleLicenseAvailabilityError(error: unknown) {
    if (error instanceof HttpErrorResponse && error.status === 400) {
      const payload = error.error as UserServiceApiErrorPayload | null

      if (payload?.code === NO_AVAILABLE_LICENSES_ERROR_CODE) {
        return throwError(() => new NoAvailableLicensesError(payload.code as string, payload.errorMessage))
      }
    }

    return throwError(() => error)
  }
}
